import React, { useEffect, useState, useRef } from 'react';
import { X, Camera, Save, User, Mail, School } from 'lucide-react';
import { clsx } from 'clsx';
import { useAuthStore } from '../../stores/authStore';
import { storage } from '../../services/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { FirestoreService } from '../../services/firestore';

const SUBJECT_OPTIONS = [
  'Mathematics',
  'Physical Sciences',
  'Natural Sciences',
  'Technical Mathematics',
  'Technical Sciences',
  'Mathematical Literacy'
];

const GRADE_OPTIONS = ['8', '9', '10', '11', '12'];

const USER_ROLE_OPTIONS = ['Learner', 'Teacher', 'Tutor', 'Official'];

const normalizeRole = (value) => String(value || '').trim().toLowerCase();

const isLearnerRole = (value) => ['learner', 'student'].includes(normalizeRole(value));
const isTeacherRole = (value) => normalizeRole(value) === 'teacher';
const isTutorRole = (value) => normalizeRole(value) === 'tutor';

const getDisplayName = (entry = {}) =>
  String(
    entry.displayName ||
    [entry.firstName, entry.lastName].filter(Boolean).join(' ') ||
    entry.email ||
    ''
  ).trim();

const normalizeArrayField = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    return value.split(',').map((item) => item.trim()).filter(Boolean);
  }
  return [];
};

const normalizeTertiarySubjects = (value) => {
  if (!Array.isArray(value) || value.length === 0) {
    return [{ name: '', yearOfStudy: '' }];
  }

  const normalized = value
    .map((item) => ({
      name: item?.name || '',
      yearOfStudy: item?.yearOfStudy || ''
    }))
    .filter((item) => item.name || item.yearOfStudy);

  return normalized.length > 0 ? normalized : [{ name: '', yearOfStudy: '' }];
};

const ProfileModal = ({ isOpen, onClose }) => {
  const { user, userData, updateUserProfile } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [isLoadingMentors, setIsLoadingMentors] = useState(false);
  const [mentorOptions, setMentorOptions] = useState({ teachers: [], tutors: [] });
  const [mentorOptionsError, setMentorOptionsError] = useState('');
  const [assignmentRequests, setAssignmentRequests] = useState([]);
  const [isLoadingRequests, setIsLoadingRequests] = useState(false);
  const [activeRequestKey, setActiveRequestKey] = useState('');
  const [errors, setErrors] = useState({});
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    firstName: userData?.firstName || '',
    lastName: userData?.lastName || '',
    institutionName: userData?.institutionName || '',
    institutionType: userData?.institutionType || 'school',
    userRole: userData?.userRole || userData?.role || 'Teacher',
    school: userData?.school || '',
    project: userData?.project || '',
    grades: normalizeArrayField(userData?.grades || userData?.gradeLevel),
    subjects: normalizeArrayField(userData?.subjects || userData?.subject),
    tertiarySubjects: normalizeTertiarySubjects(userData?.tertiarySubjects),
    teacherAssignment: userData?.teacherAssignment || null,
    tutorAssignment: userData?.tutorAssignment || null,
    bio: userData?.bio || '',
    photoURL: userData?.photoURL || user?.photoURL || ''
  });

  useEffect(() => {
    if (!isOpen) return;
    setFormData({
      firstName: userData?.firstName || '',
      lastName: userData?.lastName || '',
      institutionName: userData?.institutionName || '',
      institutionType: userData?.institutionType || 'school',
      userRole: userData?.userRole || userData?.role || 'Teacher',
      school: userData?.school || '',
      project: userData?.project || '',
      grades: normalizeArrayField(userData?.grades || userData?.gradeLevel),
      subjects: normalizeArrayField(userData?.subjects || userData?.subject),
      tertiarySubjects: normalizeTertiarySubjects(userData?.tertiarySubjects),
      teacherAssignment: userData?.teacherAssignment || null,
      tutorAssignment: userData?.tutorAssignment || null,
      bio: userData?.bio || '',
      photoURL: userData?.photoURL || user?.photoURL || ''
    });
  }, [isOpen, user, userData]);

  useEffect(() => {
    if (!isOpen || !user?.uid || !isLearnerRole(formData.userRole)) {
      setMentorOptions({ teachers: [], tutors: [] });
      setMentorOptionsError('');
      return;
    }

    const loadMentors = async () => {
      setIsLoadingMentors(true);
      setMentorOptionsError('');
      try {
        const context = {
          institutionName: formData.institutionName,
          school:
            formData.school ||
            (normalizeRole(formData.institutionType) === 'school' ? formData.institutionName : ''),
          project:
            formData.project ||
            (normalizeRole(formData.institutionType) === 'project' ? formData.institutionName : ''),
        };
        const result = await FirestoreService.findMentorCandidates(context, user.uid);
        setMentorOptions({
          teachers: result.teachers || [],
          tutors: result.tutors || [],
        });
      } catch (error) {
        console.error('Failed to load mentor options:', error);
        setMentorOptionsError('Could not load teacher and tutor options.');
      } finally {
        setIsLoadingMentors(false);
      }
    };

    loadMentors();
  }, [
    formData.institutionName,
    formData.institutionType,
    formData.project,
    formData.school,
    formData.userRole,
    isOpen,
    user?.uid,
  ]);

  useEffect(() => {
    if (!isOpen || !user?.uid || (!isTeacherRole(formData.userRole) && !isTutorRole(formData.userRole))) {
      setAssignmentRequests([]);
      return;
    }

    const loadRequests = async () => {
      setIsLoadingRequests(true);
      try {
        const results = await FirestoreService.getLearnerAssignmentRequestsForMentor(user.uid);
        setAssignmentRequests(results);
      } catch (error) {
        console.error('Failed to load learner assignment requests:', error);
      } finally {
        setIsLoadingRequests(false);
      }
    };

    loadRequests();
  }, [formData.userRole, isOpen, user?.uid]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const toggleArrayValue = (field, value) => {
    setFormData((prev) => {
      const current = prev[field] || [];
      const exists = current.includes(value);
      return {
        ...prev,
        [field]: exists
          ? current.filter((item) => item !== value)
          : [...current, value]
      };
    });
  };

  const handleMentorSelection = (type, mentorId) => {
    const list = type === 'tutor' ? mentorOptions.tutors : mentorOptions.teachers;
    const selectedMentor = list.find((entry) => entry.id === mentorId) || null;
    const assignmentKey = type === 'tutor' ? 'tutorAssignment' : 'teacherAssignment';

    setFormData((prev) => ({
      ...prev,
      [assignmentKey]: selectedMentor
        ? FirestoreService.buildMentorAssignment(selectedMentor, prev[assignmentKey], type)
        : null,
    }));
  };

  const addTertiarySubject = () => {
    setFormData((prev) => ({
      ...prev,
      tertiarySubjects: [...prev.tertiarySubjects, { name: '', yearOfStudy: '' }]
    }));
  };

  const updateTertiarySubject = (index, key, value) => {
    setFormData((prev) => ({
      ...prev,
      tertiarySubjects: prev.tertiarySubjects.map((item, i) => (
        i === index ? { ...item, [key]: value } : item
      ))
    }));
  };

  const removeTertiarySubject = (index) => {
    setFormData((prev) => {
      const next = prev.tertiarySubjects.filter((_, i) => i !== index);
      return {
        ...prev,
        tertiarySubjects: next.length > 0 ? next : [{ name: '', yearOfStudy: '' }]
      };
    });
  };

  const handlePhotoUpload = async (file) => {
    if (!file || !file.type.startsWith('image/')) {
      setErrors({ photo: 'Please select a valid image file' });
      return;
    }

    setIsUploadingPhoto(true);
    
    try {
      const timestamp = Date.now();
      const filename = `profile_images/${user.uid}_${timestamp}`;
      const storageRef = ref(storage, filename);
      
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      setFormData(prev => ({
        ...prev,
        photoURL: downloadURL
      }));
      
      setErrors(prev => ({ ...prev, photo: '' }));
    } catch (error) {
      console.error('Error uploading photo:', error);
      setErrors({ photo: 'Failed to upload photo. Please try again.' });
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      handlePhotoUpload(file);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    if (!formData.institutionName.trim()) {
      newErrors.institutionName = 'Institution name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    
    try {
      const nextRole = normalizeRole(formData.userRole);
      const payload = {
        ...formData,
        school: String(formData.school || '').trim(),
        project: String(formData.project || '').trim(),
        teacherAssignment: isLearnerRole(nextRole) ? formData.teacherAssignment || null : null,
        tutorAssignment: isLearnerRole(nextRole) ? formData.tutorAssignment || null : null,
      };
      await updateUserProfile(payload);
      onClose();
    } catch (error) {
      console.error('Error updating profile:', error);
      setErrors({ 
        general: 'Failed to update profile. Please try again.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const selectedTeacherId = formData.teacherAssignment?.userId || '';
  const selectedTutorId = formData.tutorAssignment?.userId || '';

  const getAssignmentForCurrentMentor = (learner, type) =>
    type === 'tutor' ? learner?.tutorAssignment : learner?.teacherAssignment;

  const handleConfirmLearnerAssignment = async (learnerId, type) => {
    const requestKey = `${learnerId}-${type}`;
    setActiveRequestKey(requestKey);
    try {
      await FirestoreService.updateLearnerMentorAssignmentStatus(learnerId, type, {
        status: 'confirmed',
        confirmedAt: new Date().toISOString(),
        confirmedBy: user?.uid || null,
      });
      setAssignmentRequests((current) =>
        current.map((entry) => {
          if (entry.id !== learnerId) return entry;
          const key = type === 'tutor' ? 'tutorAssignment' : 'teacherAssignment';
          return {
            ...entry,
            [key]: {
              ...(entry[key] || {}),
              status: 'confirmed',
              confirmedAt: new Date().toISOString(),
              confirmedBy: user?.uid || null,
            },
          };
        })
      );
    } catch (error) {
      console.error('Failed to confirm learner assignment:', error);
    } finally {
      setActiveRequestKey('');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Edit Profile</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* General Error */}
          {errors.general && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {errors.general}
            </div>
          )}

          {userData?.requiresProfileCompletion && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
              Complete the required profile details to unlock AI Content Creator and script marking uploads.
            </div>
          )}

          {/* Profile Photo */}
          <div className="mb-6 flex items-center space-x-6">
            <div className="relative">
              <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                {formData.photoURL ? (
                  <img
                    src={formData.photoURL}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="w-8 h-8 text-gray-400" />
                )}
              </div>
              
              {isUploadingPhoto && (
                <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                </div>
              )}
            </div>
            
            <div>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingPhoto}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                <Camera className="w-4 h-4 mr-2" />
                Change Photo
              </button>
              <p className="text-xs text-gray-500 mt-1">
                JPG, PNG up to 5MB
              </p>
              {errors.photo && (
                <p className="text-red-500 text-xs mt-1">{errors.photo}</p>
              )}
            </div>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {/* Personal Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className={clsx(
                  'w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-sigma-500 focus:border-sigma-500',
                  errors.firstName ? 'border-red-300' : 'border-gray-300'
                )}
                placeholder="First name"
              />
              {errors.firstName && (
                <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className={clsx(
                  'w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-sigma-500 focus:border-sigma-500',
                  errors.lastName ? 'border-red-300' : 'border-gray-300'
                )}
                placeholder="Last name"
              />
              {errors.lastName && (
                <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>
              )}
            </div>
          </div>

          {/* Email (Read-only) */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="email"
                value={user?.email || ''}
                disabled
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Email cannot be changed. Contact support if needed.
            </p>
          </div>

          {/* Institution Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Institution Type
              </label>
              <select
                name="institutionType"
                value={formData.institutionType}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sigma-500 focus:border-sigma-500"
              >
                <option value="school">School</option>
                <option value="district">District</option>
                <option value="project">Project</option>
                <option value="college">College</option>
                <option value="university">University</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                User Role
              </label>
              <select
                name="userRole"
                value={formData.userRole}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sigma-500 focus:border-sigma-500"
              >
                {USER_ROLE_OPTIONS.map((role) => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                School
              </label>
              <input
                type="text"
                name="school"
                value={formData.school}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sigma-500 focus:border-sigma-500"
                placeholder="School name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Project
              </label>
              <input
                type="text"
                name="project"
                value={formData.project}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sigma-500 focus:border-sigma-500"
                placeholder="Project or programme"
              />
            </div>
          </div>

          {isLearnerRole(formData.userRole) && (
            <div className="mb-6 rounded-xl border border-sigma-100 bg-sigma-50/60 p-4">
              <div className="mb-3">
                <h3 className="text-sm font-semibold text-gray-900">Teacher and Tutor Links</h3>
                <p className="text-xs text-gray-500 mt-1">
                  Select your teacher and/or tutor from the same school or project. They must confirm the link before grading workflows rely on it.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Teacher
                  </label>
                  <select
                    value={selectedTeacherId}
                    onChange={(e) => handleMentorSelection('teacher', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-sigma-500 focus:border-sigma-500"
                  >
                    <option value="">Select teacher</option>
                    {mentorOptions.teachers.map((mentor) => (
                      <option key={mentor.id} value={mentor.id}>
                        {`${getDisplayName(mentor)}${mentor.school ? ` • ${mentor.school}` : mentor.project ? ` • ${mentor.project}` : ''}`}
                      </option>
                    ))}
                  </select>
                  {formData.teacherAssignment?.status && (
                    <p className="mt-1 text-xs text-gray-500">
                      Status: <span className="font-medium capitalize">{formData.teacherAssignment.status}</span>
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tutor
                  </label>
                  <select
                    value={selectedTutorId}
                    onChange={(e) => handleMentorSelection('tutor', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-sigma-500 focus:border-sigma-500"
                  >
                    <option value="">Select tutor</option>
                    {mentorOptions.tutors.map((mentor) => (
                      <option key={mentor.id} value={mentor.id}>
                        {`${getDisplayName(mentor)}${mentor.school ? ` • ${mentor.school}` : mentor.project ? ` • ${mentor.project}` : ''}`}
                      </option>
                    ))}
                  </select>
                  {formData.tutorAssignment?.status && (
                    <p className="mt-1 text-xs text-gray-500">
                      Status: <span className="font-medium capitalize">{formData.tutorAssignment.status}</span>
                    </p>
                  )}
                </div>
              </div>

              {isLoadingMentors && (
                <p className="mt-3 text-xs text-gray-500">Loading teachers and tutors...</p>
              )}
              {!isLoadingMentors && !mentorOptions.teachers.length && !mentorOptions.tutors.length && (
                <p className="mt-3 text-xs text-amber-700">
                  No teachers or tutors were found for your current school or project.
                </p>
              )}
              {mentorOptionsError && (
                <p className="mt-3 text-xs text-red-600">{mentorOptionsError}</p>
              )}
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Institution Name
            </label>
            <div className="relative">
              <School className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                name="institutionName"
                value={formData.institutionName}
                onChange={handleInputChange}
                className={clsx(
                  'w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-sigma-500 focus:border-sigma-500',
                  errors.institutionName ? 'border-red-300' : 'border-gray-300'
                )}
                placeholder="Your school/college/university name"
              />
            </div>
            {errors.institutionName && (
              <p className="text-red-500 text-xs mt-1">{errors.institutionName}</p>
            )}
          </div>

          {(formData.institutionType === 'college' || formData.institutionType === 'university') ? (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Subjects and Year of Study
                </label>
                <button
                  type="button"
                  onClick={addTertiarySubject}
                  className="text-sm text-sigma-600 hover:text-sigma-700"
                >
                  + Add subject
                </button>
              </div>

              <div className="space-y-2">
                {formData.tertiarySubjects.map((entry, index) => (
                  <div key={`tertiary-${index}`} className="grid grid-cols-1 md:grid-cols-5 gap-2">
                    <div className="md:col-span-3">
                      <input
                        type="text"
                        value={entry.name}
                        onChange={(e) => updateTertiarySubject(index, 'name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sigma-500 focus:border-sigma-500"
                        placeholder="Subject name"
                      />
                    </div>
                    <div className="md:col-span-1">
                      <input
                        type="text"
                        value={entry.yearOfStudy}
                        onChange={(e) => updateTertiarySubject(index, 'yearOfStudy', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sigma-500 focus:border-sigma-500"
                        placeholder="Year"
                      />
                    </div>
                    <div className="md:col-span-1">
                      <button
                        type="button"
                        onClick={() => removeTertiarySubject(index)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subjects (Select all that apply)
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {SUBJECT_OPTIONS.map((subject) => (
                    <label key={subject} className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={formData.subjects.includes(subject)}
                        onChange={() => toggleArrayValue('subjects', subject)}
                        className="rounded border-gray-300 text-sigma-600 focus:ring-sigma-500"
                      />
                      {subject}
                    </label>
                  ))}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Grades (Select all that apply)
                </label>
                <div className="flex flex-wrap gap-3">
                  {GRADE_OPTIONS.map((grade) => (
                    <label key={grade} className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={formData.grades.includes(grade)}
                        onChange={() => toggleArrayValue('grades', grade)}
                        className="rounded border-gray-300 text-sigma-600 focus:ring-sigma-500"
                      />
                      Grade {grade}
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Bio */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bio
            </label>
            <textarea
              name="bio"
              value={formData.bio}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sigma-500 focus:border-sigma-500"
              placeholder="Tell us a bit about yourself..."
            />
          </div>

          {/* User Status Display */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">Account Status</p>
                <p className={clsx(
                  'text-sm',
                  userData?.isApproved 
                    ? 'text-green-600' 
                    : 'text-yellow-600'
                )}>
                  {userData?.isApproved ? 'Approved' : 'Pending Approval'}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">Profile</p>
                <p className={clsx(
                  'text-sm',
                  userData?.requiresProfileCompletion ? 'text-amber-600' : 'text-green-600'
                )}>
                  {userData?.requiresProfileCompletion ? 'Incomplete' : 'Complete'}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">Role</p>
                <p className="text-sm text-gray-600 capitalize">
                  {userData?.role || 'Student'}
                </p>
              </div>
            </div>
          </div>

          {(isTeacherRole(formData.userRole) || isTutorRole(formData.userRole)) && (
            <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4">
              <div className="mb-3">
                <h3 className="text-sm font-semibold text-gray-900">Learner Link Requests</h3>
                <p className="text-xs text-gray-500 mt-1">
                  Confirm learners who selected you in their profile so grading access can be routed correctly.
                </p>
              </div>

              {isLoadingRequests ? (
                <p className="text-sm text-gray-500">Loading learner requests...</p>
              ) : assignmentRequests.length === 0 ? (
                <p className="text-sm text-gray-500">No learner requests yet.</p>
              ) : (
                <div className="space-y-3">
                  {assignmentRequests.map((learner) => {
                    const teacherAssignment = getAssignmentForCurrentMentor(learner, 'teacher');
                    const tutorAssignment = getAssignmentForCurrentMentor(learner, 'tutor');
                    const relevantAssignments = [
                      isTeacherRole(formData.userRole) ? { type: 'teacher', value: teacherAssignment } : null,
                      isTutorRole(formData.userRole) ? { type: 'tutor', value: tutorAssignment } : null,
                    ].filter(Boolean).filter((entry) => entry.value?.userId === user?.uid);

                    if (!relevantAssignments.length) return null;

                    return (
                      <div key={learner.id} className="rounded-lg border border-gray-200 p-3">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{getDisplayName(learner)}</div>
                            <div className="text-xs text-gray-500">
                              {learner.email || 'No email'}{learner.school ? ` • ${learner.school}` : ''}{learner.project ? ` • ${learner.project}` : ''}
                            </div>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            {relevantAssignments.map(({ type, value }) => {
                              const isPending = value?.status !== 'confirmed';
                              return (
                                <div key={`${learner.id}-${type}`} className="flex items-center gap-2">
                                  <span className="rounded-full bg-gray-100 px-2 py-1 text-[11px] font-medium uppercase tracking-wide text-gray-600">
                                    {type}
                                  </span>
                                  {isPending ? (
                                    <button
                                      type="button"
                                      onClick={() => handleConfirmLearnerAssignment(learner.id, type)}
                                      disabled={activeRequestKey === `${learner.id}-${type}`}
                                      className="rounded-lg bg-sigma-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-sigma-800 disabled:cursor-not-allowed disabled:bg-gray-300"
                                    >
                                      {activeRequestKey === `${learner.id}-${type}` ? 'Confirming...' : 'Confirm'}
                                    </button>
                                  ) : (
                                    <span className="rounded-lg bg-emerald-100 px-3 py-1.5 text-xs font-semibold text-emerald-700">
                                      Confirmed
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className={clsx(
                'px-4 py-2 rounded-lg font-medium transition-colors',
                'bg-sigma-600 text-white hover:bg-sigma-700',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                'flex items-center'
              )}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfileModal;
